import React, { useState, useEffect } from "react"
import { useRouter } from "next/router"
import { apim } from "@/constants/api"
import { getConfig } from "@/constants/config"
import { store } from "@/store"
import _get from "lodash/get"
import { useSelector } from "react-redux"
import { selectAuthState } from "@/store/features/authSlice"
import { getLineItemAttrs } from "@/utils/product"
import ReturnSummary from "./ReturnSummary/ReturnSummary"
import ReturnItemList from "./ReturnList/ReturnItemList"
import FaultyProducts from "./FaultyProducts/FaultyProducts"
import Review from "./Review/Review"
import ContactInformation from "./ContactInformation/ContactInformation"
import styles from "./returns.module.scss"
import arrowLeft from "@/public/icons/back-arrow.svg"
import StepNavigator from "@/components/core/StepNavigator/StepNavigator"
import ReturnConfirmation from "@/components/Returns/v1/ReturnConfirmation/ReturnConfirmation"
import { getTranslations } from "@/components/Returns/v1/localization"
import { useTranslation } from "next-i18next"
import PageLoader from "@/components/core/PageLoader/PageLoader"
import Modal from "@/components/core/Modal/Modal"
import Button from "@/components/core/Button/Button"
import {
  getGenericListPath,
  getReasonList,
  selectGenericState,
} from "@/store/features/genericSlice"
import { addReturnAnalytics } from "./analytics"

const Returns = () => {
  const [data, setData] = useState({})
  const [orderSummary, setOrderSummary] = useState({})
  const [isSuccess, setIsSuccess] = useState(false)
  const [config, setConfig] = useState({})
  const [returnedItems, setReturnedItems] = useState({
    price: 0,
    quantity: 1,
    returnData: "",
    quantityTotalPrice: "",
    isUpdated: false,
    productName: "",
    productId: "",
    productDescription: "",
    productDimension: "",
    productCategory: "",
  })
  const [faulyData, setFaultyData] = useState([])
  const [dataLoading, setDataLoading] = useState(true)
  const [isShowModal, setIsShowModal] = useState(false)
  const { user: { sub: userEmail } = {} } = useSelector(selectAuthState)
  const { reasonList = [] } = useSelector(selectGenericState)

  const router = useRouter()
  const { t } = useTranslation("common")
  const staticText = getTranslations(t)
  const { orderNumber, shipmentId, sku } = router.query

  useEffect(() => {
    getConfig().then(list => {
      setConfig(list)
    })
    getOrder()
  }, [])
  const handleDiscount = (discount, quantityVal, selectedQuantity) => {
    const getDiscount = (discount / quantityVal) * selectedQuantity
    return getDiscount
  }

  const handleTax = (tax, priceVal) => {
    const getTaxPercentage = parseFloat(tax) + 100
    const getTax = Number((tax / getTaxPercentage) * priceVal)?.toFixed(2)
    return getTax ?? 0
  }

  const handleMRP = (price, quantity) => {
    const getMRP = price * quantity
    return getMRP
  }

  const getOrder = async () => {
    await store.dispatch(getGenericListPath())
    await store.dispatch(getReasonList())
    const payload = {
      email: userEmail,
      orderId: orderNumber,
    }
    try {
      const res = await apim.post(`checkout/orders/v1/query`, payload)
      if (res) {
        let shipments =
          res?.data?.order_detail?.shipments?.filter(
            item => item.shipDetail[0]?.trackingNumber === shipmentId
          )[0] ?? {}
        let lineItems = shipments?.shipDetail[0]?.item?.filter(
          item => item.sku === sku
        )
        const lwCartItems = await getLineItemAttrs(lineItems ?? [])
        lineItems?.forEach(order => {
          const { lwData } = lwCartItems.find(prod => prod.sku === order.sku)
          order.lwData = lwData
        })
        const priceVal =
          shipments?.shipDetail[0]?.item?.filter(item => item.sku === sku)[0]
            ?.price ?? 0
        const productName =
          shipments?.shipDetail[0]?.item?.filter(item => item.sku === sku)[0]
            ?.itemName ?? ""
        const productId =
          shipments?.shipDetail[0]?.item?.filter(item => item.sku === sku)[0]
            ?.sku ?? ""
        const productDescription =
          shipments?.shipDetail[0]?.item?.filter(item => item.sku === sku)[0]
            ?.udf2 ?? ""
        const productDimension =
          shipments?.shipDetail[0]?.item?.filter(item => item.sku === sku)[0]
            ?.description ?? ""
        const productCategory = lwCartItems.find(prod => prod.sku === sku)
          ?.lwData?.Product_Category
        const quantityVal =
          Object.keys(shipments)?.length > 0
            ? shipments?.shipDetail[0]?.item?.filter(
                list => list.sku === sku
              )[0]?.shippedQty
            : 0

        const currencySign = config?.internationalization?.currencySign ?? ""

        const individualData =
          Object.keys(shipments)?.length > 0
            ? shipments?.shipDetail[0]?.item?.filter(
                list => list.sku === sku
              )[0]
            : {}
        const {
          price = "",
          discAmount = "",
          taxPercentage = "",
        } = individualData
        const discountAmount = handleDiscount(
          discAmount,
          quantityVal,
          returnedItems?.quantity
        )
        const refundTotal =
          handleMRP(price, returnedItems?.quantity) -
          handleDiscount(discAmount, quantityVal, returnedItems?.quantity)
        const tax = handleTax(
          taxPercentage,
          handleMRP(price, returnedItems?.quantity) -
            handleDiscount(discAmount, quantityVal, returnedItems?.quantity)
        )
        const basePrice = refundTotal - +tax

        setReturnedItems({
          ...returnedItems,
          price: Number(priceVal)?.toFixed(2),
          quantityTotalPrice: Number(priceVal)?.toFixed(2),
          productName,
          productId,
          productDescription,
          productDimension,
          productCategory,
          basePrice,
          taxAmount: tax,
          totalAmount: refundTotal,
          currency: currencySign,
        })
        setData(shipments ?? {})
        setOrderSummary(res?.data?.order_detail ?? {})
        setDataLoading(false)
      }
    } catch (err) {
      console.log(err)
      setDataLoading(false)
    }
  }

  const handleSubmit = async () => {
    try {
      const payload = {
        returnRequestData: {
          orderNumber: data?.extOrderNo ?? "",
          items: {
            quantity: returnedItems?.quantity,
            comment:
              returnedItems?.returnData !== ""
                ? returnedItems?.returnData
                : staticText.noReason,
            sku: sku,
            price: returnedItems?.price ?? "",
            trackingno: shipmentId,
            invoiceno: Object.keys(data)?.length
              ? data?.shipDetail[0]?.invoiceNo
              : "",
            images: faulyData,
          },
        },
      }
      const res = await apim.post(`/return/create?type=single`, payload)
      if (res) {
        setIsSuccess(true)
      }
    } catch (err) {
      console.log(err)
    }
  }
  const steps = [
    {
      label: staticText.returnItem,
      content: ReturnItemList,
      props: {
        data: Object.keys(data)?.length > 0 ? data : {},
        orderData: orderSummary ?? {},
        currencySign: config?.internationalization?.currencySign ?? "",
        brandName: Object.keys(data)?.length > 0 ? data?.owner : "",
        skuId: sku,
        returnedItems: returnedItems,
        reasonList: reasonList,
        getItems: (price, quantity, quantityTotalPrice) =>
          setReturnedItems({
            ...returnedItems,
            price: price,
            quantity: quantity,
            quantityTotalPrice: quantityTotalPrice,
          }),
        handleReturnData: val =>
          setReturnedItems({
            ...returnedItems,
            returnData: val,
          }),
        handleUpdate: val => {
          setReturnedItems({ ...returnedItems, isUpdated: val })
          addReturnAnalytics(returnedItems, "one")
        },
      },
    },
    {
      label: staticText.faulty,
      content: FaultyProducts,
      props: {
        handleImages: addRows => {
          setFaultyData(addRows)
          addReturnAnalytics(returnedItems, "two", addRows)
        },
      },
    },
    {
      label: staticText.contactInfo,
      content: ContactInformation,
      props: {
        data: orderSummary?.shipping_address ?? {},
        accountSettingsPath: config?.general?.accountSettingsPath ?? "/",
        handleOnNextStep: () => {
          addReturnAnalytics(returnedItems, "three")
        },
      },
    },
    {
      label: staticText.review,
      content: Review,
      props: {
        onReview: () => {
          handleSubmit()
          addReturnAnalytics(returnedItems, "four")
        },
      },
    },
  ]

  const handleRedirection = () => {
    if (returnedItems?.isUpdated) {
      setIsShowModal(true)
    } else {
      redirectionToOrder()
    }
  }

  const redirectionToOrder = () => {
    setIsShowModal(false)
    const redirectionLink = `${config?.general?.orderDetailsPath}?orderNumber=${orderNumber}`
    window.location.href = redirectionLink
  }

  return (
    <div className={styles.returnRequest}>
      {dataLoading ? (
        <div className="page-load-wrapper">
          <PageLoader loading={dataLoading} />
        </div>
      ) : !isSuccess ? (
        <div className="container kf-react-container return-request">
          <div className="returnItem__back">
            <img src={arrowLeft?.src} alt="Back" />
            <p onClick={handleRedirection}>{staticText.backToOrder}</p>
          </div>

          <div className="return-request__header-wrapper">
            <div className="return-request__items-wrapper">
              {Object.keys(data)?.length > 0 ? (
                <p className="returnItem__title">
                  {staticText.returnRequestOrder(
                    orderNumber,
                    orderSummary?.shipments
                      ?.map((item, index) => ({ ...item, index }))
                      .filter(
                        list =>
                          list.shipDetail[0]?.trackingNumber === shipmentId
                      )
                      .map(item => item.index + 1),
                    orderSummary?.shipments?.length
                  )}
                </p>
              ) : null}
              {Object.keys(data)?.length ? (
                <StepNavigator
                  data={
                    data?.shipDetail[0]?.item?.filter(
                      item => item.sku === sku
                    )[0]?.udf2 === "NA for service"
                      ? steps?.filter(item => item.label !== staticText.faulty)
                      : steps
                  }
                />
              ) : null}
            </div>
            <div className="return-request__summary-wrapper">
              <ReturnSummary
                data={
                  Object.keys(data)?.length > 0
                    ? data?.shipDetail[0]?.item?.filter(
                        list => list.sku === sku
                      )[0]
                    : {}
                }
                quantityVal={
                  Object.keys(data)?.length > 0
                    ? data?.shipDetail[0]?.item?.filter(
                        list => list.sku === sku
                      )[0]?.shippedQty
                    : 0
                }
                currencySign={config?.internationalization?.currencySign ?? ""}
                returnedItems={returnedItems}
                tax={orderSummary?.order_summary?.tax ?? 0}
              />
            </div>
          </div>
          {isShowModal ? (
            <Modal
              showModal={isShowModal}
              onModalClose={() => setIsShowModal(false)}
            >
              <h3>{staticText.areYou}</h3>
              <p>{staticText.allProgress}</p>
              <div className="return__modal-cta">
                <Button
                  type="black"
                  role="button"
                  label={staticText.yes}
                  onClick={redirectionToOrder}
                  size={"small"}
                  className="return__modal-button"
                />
                <Button
                  type="ghost"
                  role="button"
                  label={staticText.no}
                  size={"small"}
                  onClick={() => setIsShowModal(false)}
                  className="return__modal-button"
                />
              </div>
            </Modal>
          ) : null}
        </div>
      ) : (
        <ReturnConfirmation
          faulyData={faulyData}
          data={Object.keys(data)?.length > 0 ? data?.shipDetail[0] : {}}
          returnedItems={returnedItems}
          skuId={sku}
          currencySign={config?.internationalization?.currencySign ?? ""}
          brandName={Object.keys(data)?.length > 0 ? data?.owner : ""}
          isReturns={isSuccess}
          curstep={1}
          orderNumber={orderNumber}
          orderDetailsUrl={config?.general?.orderDetailsPath ?? "/"}
          quantityVal={
            Object.keys(data)?.length > 0
              ? data?.shipDetail[0]?.item?.filter(list => list.sku === sku)[0]
                  ?.shippedQty
              : 0
          }
          orderSummary={orderSummary}
        />
      )}
    </div>
  )
}

export { Returns as ReturnItems }
